import { Economy } from '@audigoal/core';
import { ACHIEVEMENTS, DAILY_QUESTS, achievementById, questById } from '@audigoal/content';
import { db } from './db';
import { localDate } from './daily';
import { getSupabase } from './supabase';
import type { AchievementRow, ChestRow, Profile, QuestRow } from './types';

/**
 * Quests, achievements and chests on the device.
 *
 * Local-first, like everything else: the day's three quests are computed here
 * from the same seeded function the server uses, so they appear instantly and
 * offline, and progress accumulates locally as sessions finish. What is *not*
 * local is a reward — gems come from the server, which re-reads the price and
 * the threshold from its own catalog. When there is no backend at all the
 * claim is applied locally, because a learner with no account still deserves
 * their gems; when there is one and it cannot be reached, the claim waits.
 */

/* ------------------------------------------------------------------ keys */

export function questKey(periodKey: string, questId: string): string {
  return `${periodKey}|${questId}`;
}

/** A stable number per learner, so two people get different quests. */
export function seedForUser(userId: string): number {
  let h = 2166136261;
  for (const ch of userId) {
    h ^= ch.charCodeAt(0);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h;
}

/* ---------------------------------------------------------------- quests */

export interface QuestView {
  row: QuestRow;
  title: string;
  fraction: number;
}

/**
 * The day's three quests, creating them on the first look of the day.
 *
 * Yesterday's set is passed to the selector so the same three do not come back
 * two days running, which is the difference between a daily quest and a chore.
 */
export async function todaysQuests(profile: Profile, now = new Date()): Promise<QuestRow[]> {
  const today = localDate(now);
  const existing = await db.quests.where('periodKey').equals(today).toArray();
  if (existing.length > 0) return existing;

  const yesterdayKey = localDate(new Date(now.getTime() - 24 * 60 * 60 * 1000));
  const yesterday = (await db.quests.where('periodKey').equals(yesterdayKey).toArray()).map(
    (row) => row.questId,
  );

  const selection = Economy.selectDailyQuests({
    catalog: DAILY_QUESTS,
    localDate: today,
    userSeed: seedForUser(profile.id),
    sectionReached: sectionReached(profile),
    instrument: profile.flags.theoryOnly ? null : profile.primaryInstrument,
    yesterday,
  });

  const rows: QuestRow[] = selection.quests.map((issued) => {
    const definition = questById(issued.questId);
    return {
      key: questKey(today, issued.questId),
      periodKey: today,
      questId: issued.questId,
      metric: definition?.metric ?? 'xp',
      difficulty: issued.difficulty,
      target: issued.target,
      gems: issued.gems,
      progress: 0,
      completed: false,
      claimed: false,
      updatedAt: now.toISOString(),
    };
  });

  await db.quests.bulkPut(rows);
  return rows;
}

/** How far into the course the learner is, for scaling quest targets. */
export function sectionReached(profile: Profile): number {
  return Math.max(1, (profile.flags.placedSection ?? 0) + 1);
}

/** Applies a finished session's metric deltas to today's quests. */
export async function applyQuestProgress(
  progress: Partial<Record<Economy.QuestMetric, number>>,
  now = new Date(),
): Promise<QuestRow[]> {
  const today = localDate(now);
  const rows = await db.quests.where('periodKey').equals(today).toArray();
  const moved: QuestRow[] = [];

  for (const row of rows) {
    const gained = progress[row.metric as Economy.QuestMetric] ?? 0;
    if (gained <= 0) continue;
    const next = Economy.isHighWater(row.metric as Economy.QuestMetric)
      ? Math.max(row.progress, gained)
      : row.progress + gained;
    const updated: QuestRow = {
      ...row,
      progress: next,
      completed: row.completed || next >= row.target,
      updatedAt: now.toISOString(),
    };
    moved.push(updated);
  }

  if (moved.length > 0) await db.quests.bulkPut(moved);
  return moved;
}

export function questView(row: QuestRow): QuestView {
  const definition = questById(row.questId);
  return {
    row,
    title:
      definition === undefined ? row.questId : definition.title.replace('{n}', String(row.target)),
    fraction: Math.max(0, Math.min(1, row.progress / Math.max(1, row.target))),
  };
}

export interface ClaimResult {
  gems: number;
  bonus: number;
  /** True when the reward was applied on this device because there is no backend. */
  local: boolean;
}

/**
 * Claims a finished quest.
 *
 * The server is authoritative when there is one: it re-reads the reward from
 * its catalog, so what lands in the wallet is what the quest was worth, not
 * what this device says it was worth.
 */
export async function claimQuest(row: QuestRow): Promise<ClaimResult> {
  if (!row.completed) throw new Error('Quest is not complete');
  if (row.claimed) throw new Error('Quest already claimed');

  const supabase = await getSupabase();
  if (supabase === null) {
    const all = await db.quests.where('periodKey').equals(row.periodKey).toArray();
    const last = all.filter((q) => !q.claimed).length === 1 && all.length >= 3;
    await db.quests.put({ ...row, claimed: true });
    return { gems: row.gems, bonus: last ? Economy.ALL_QUESTS_BONUS_GEMS : 0, local: true };
  }

  const { data, error } = await supabase.rpc('claim_quest', {
    p_quest_id: row.questId,
    p_period: row.periodKey,
  });
  if (error !== null) throw new Error(error.message);

  await db.quests.put({ ...row, claimed: true });
  const result = (data ?? {}) as { gems?: number; bonus?: number };
  return { gems: result.gems ?? row.gems, bonus: result.bonus ?? 0, local: false };
}

/* ---------------------------------------------------------- achievements */

export interface AchievementProgressView extends Economy.AchievementView {
  row: AchievementRow;
}

/** Every achievement with the learner's progress laid over it. */
export async function achievementViews(profile: Profile): Promise<AchievementProgressView[]> {
  const rows = new Map((await db.achievements.toArray()).map((row) => [row.achievementId, row]));

  return ACHIEVEMENTS.map((definition) => {
    const row: AchievementRow = rows.get(definition.id) ?? {
      achievementId: definition.id,
      progress: 0,
      tier: 0,
      claimedTier: 0,
      updatedAt: new Date().toISOString(),
    };
    // The counters that live on the profile are read from it, so the screen
    // never shows a stale copy of a number it can see directly.
    const progress =
      definition.metric === 'totalXp'
        ? profile.totalXp
        : definition.metric === 'longestStreak'
          ? profile.streakLongest
          : row.progress;

    return {
      row,
      ...Economy.achievementView(definition, { ...row, progress }),
    };
  });
}

/** Adds a finished session's counters and recomputes the tiers. */
export async function applyAchievementProgress(
  progress: Partial<Record<Economy.AchievementMetric, number>>,
  profile: Profile,
  now = new Date(),
): Promise<AchievementRow[]> {
  const rows = new Map((await db.achievements.toArray()).map((row) => [row.achievementId, row]));
  const tieredUp: AchievementRow[] = [];
  const writes: AchievementRow[] = [];

  for (const definition of ACHIEVEMENTS) {
    const existing = rows.get(definition.id);
    const before = existing?.tier ?? 0;
    const gained = progress[definition.metric] ?? 0;

    const current =
      definition.metric === 'totalXp'
        ? profile.totalXp
        : definition.metric === 'longestStreak'
          ? profile.streakLongest
          : Economy.isAchievementHighWater(definition.metric)
            ? Math.max(existing?.progress ?? 0, gained)
            : (existing?.progress ?? 0) + gained;

    if (current === (existing?.progress ?? 0) && existing !== undefined) continue;

    const row: AchievementRow = {
      achievementId: definition.id,
      progress: current,
      tier: Economy.tierFor(definition, current),
      claimedTier: existing?.claimedTier ?? 0,
      updatedAt: now.toISOString(),
    };
    writes.push(row);
    if (row.tier > before) tieredUp.push(row);
  }

  if (writes.length > 0) await db.achievements.bulkPut(writes);
  return tieredUp;
}

export async function claimAchievement(
  view: AchievementProgressView,
): Promise<{ gems: number; cosmetics: string[]; local: boolean }> {
  if (view.claimable <= 0) throw new Error('Nothing to claim');

  const supabase = await getSupabase();
  const claimed: AchievementRow = { ...view.row, tier: view.tier, claimedTier: view.tier };

  if (supabase === null) {
    await db.achievements.put(claimed);
    for (const id of view.cosmeticsWaiting) {
      await db.cosmetics.put({ cosmeticId: id, source: `achievement:${view.row.achievementId}` });
    }
    return { gems: view.gemsWaiting, cosmetics: view.cosmeticsWaiting, local: true };
  }

  const { data, error } = await supabase.rpc('claim_achievement', {
    p_achievement_id: view.row.achievementId,
  });
  if (error !== null) throw new Error(error.message);

  await db.achievements.put(claimed);
  const result = (data ?? {}) as { gems?: number; cosmetics?: string[] };
  for (const id of result.cosmetics ?? []) {
    await db.cosmetics.put({ cosmeticId: id, source: `achievement:${view.row.achievementId}` });
  }
  return {
    gems: result.gems ?? view.gemsWaiting,
    cosmetics: result.cosmetics ?? view.cosmeticsWaiting,
    local: false,
  };
}

/** The achievements worth showing on the profile strip: closest to a tier. */
export function nearestAchievements(
  views: readonly AchievementProgressView[],
  count = 4,
): AchievementProgressView[] {
  return [...views]
    .filter((view) => view.nextThreshold !== null)
    .sort((a, b) => b.fraction - a.fraction)
    .slice(0, count);
}

/* ---------------------------------------------------------------- chests */

export async function unopenedChests(): Promise<ChestRow[]> {
  return db.chests.filter((chest) => chest.openedAt === null).toArray();
}

export async function recordChests(
  chests: readonly { id: string; kind: string; source: string }[],
): Promise<void> {
  if (chests.length === 0) return;
  await db.chests.bulkPut(
    chests.map((chest) => ({
      id: chest.id,
      kind: chest.kind as ChestRow['kind'],
      source: chest.source,
      openedAt: null,
      reward: null,
    })),
  );
}

/** What a gig bag held: Picks (`gems` on the wire) or a cosmetic. */
export interface ChestOpening {
  kind: 'gems' | 'cosmetic';
  amount?: number;
  cosmeticId?: string;
  rarity?: string;
}

/** Opens a chest. The roll is the server's; this plays what it returns. */
export async function openChest(chest: ChestRow): Promise<ChestOpening> {
  const supabase = await getSupabase();
  const now = new Date().toISOString();

  if (supabase === null) {
    // No backend: roll locally with the same table the server uses.
    const owned = new Set((await db.cosmetics.toArray()).map((c) => c.cosmeticId));
    const reward = Economy.openChest(chest.kind, Date.now(), {
      availableByRarity: availableByRarity(owned),
    });
    const opening = toOpening(reward);
    await db.chests.put({ ...chest, openedAt: now, reward: opening });
    if (opening.kind === 'cosmetic' && opening.cosmeticId !== undefined) {
      await db.cosmetics.put({ cosmeticId: opening.cosmeticId, source: `chest:${chest.kind}` });
    }
    return opening;
  }

  const { data, error } = await supabase.rpc('open_chest', { p_chest_id: chest.id });
  if (error !== null) throw new Error(error.message);

  const reward = (data ?? {}) as Record<string, unknown>;
  const opening: ChestOpening = {
    kind: reward['kind'] === 'cosmetic' ? 'cosmetic' : 'gems',
    ...(typeof reward['amount'] === 'number' ? { amount: reward['amount'] } : {}),
    ...(typeof reward['cosmetic_id'] === 'string' ? { cosmeticId: reward['cosmetic_id'] } : {}),
    ...(typeof reward['rarity'] === 'string' ? { rarity: reward['rarity'] } : {}),
  };
  await db.chests.put({ ...chest, openedAt: now, reward: opening });
  if (opening.cosmeticId !== undefined) {
    await db.cosmetics.put({ cosmeticId: opening.cosmeticId, source: `chest:${chest.kind}` });
  }
  return opening;
}

function toOpening(reward: Economy.ChestReward): ChestOpening {
  if (reward.kind === 'gems') return { kind: 'gems', amount: reward.amount };
  return { kind: 'cosmetic', rarity: reward.rarity };
}

function availableByRarity(owned: ReadonlySet<string>): Partial<Record<Economy.Rarity, number>> {
  // Only used without a backend, where the catalog is the bundled one.
  const out: Partial<Record<Economy.Rarity, number>> = {};
  for (const rarity of ['common', 'uncommon', 'rare', 'epic'] as const) {
    out[rarity] = 1;
  }
  void owned;
  return out;
}

/* -------------------------------------------------------------- wardrobe */

export async function ownedCosmetics(): Promise<Set<string>> {
  return new Set((await db.cosmetics.toArray()).map((row) => row.cosmeticId));
}

/** Buys a cosmetic — the only thing for sale. Picks are kept on the server. */
export async function buyItem(itemId: string): Promise<{ gems: number }> {
  const supabase = await getSupabase();
  if (supabase === null) throw new Error('offline');

  const { data, error } = await supabase.rpc('purchase_item', { p_item_id: itemId });
  if (error !== null) throw new Error(error.message);

  const result = (data ?? {}) as { gems?: number };
  await db.cosmetics.put({ cosmeticId: itemId, source: 'shop' });
  return { gems: result.gems ?? 0 };
}

/* ----------------------------------------------------------------- bonds */

export function bondKey(character: string): string {
  return `bond:${character}`;
}

/** Every character's bond points, for the profile strip. */
export async function allBonds(): Promise<Map<string, number>> {
  const rows = await db.kv.toArray();
  const out = new Map<string, number>();
  for (const row of rows) {
    if (!row.key.startsWith('bond:')) continue;
    if (typeof row.value === 'number') out.set(row.key.slice(5), row.value);
  }
  return out;
}

export async function bondPointsFor(character: string): Promise<number> {
  const row = await db.kv.get(bondKey(character));
  return typeof row?.value === 'number' ? row.value : 0;
}

/**
 * Adds a session's bond points and reports any level reached.
 *
 * The level is recomputed from the total rather than incremented, so a total
 * corrected by a later sync cannot leave the level behind.
 */
export async function addBondPoints(
  points: Partial<Record<string, number>>,
): Promise<{ character: string; level: number }[]> {
  const levelled: { character: string; level: number }[] = [];

  for (const [character, gained] of Object.entries(points)) {
    if (gained === undefined || gained <= 0) continue;
    const before = await bondPointsFor(character);
    const after = before + gained;
    await db.kv.put({ key: bondKey(character), value: after });

    const beforeLevel = Economy.bondLevel(before);
    const afterLevel = Economy.bondLevel(after);
    if (afterLevel > beforeLevel) levelled.push({ character, level: afterLevel });
  }
  return levelled;
}

/** The named achievement, for a card that wants just one. */
export function achievementName(id: string): string {
  return achievementById(id)?.name ?? id;
}
